// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Mutation hook for claiming an alarm or releasing it.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendAlarmAssignment } from '../services/alarmsService';
import type { AlarmAssignmentAction } from '../services/alarmApiService';
import { ApiError } from '../services/api';

export interface AlarmAssignmentVars {
  siteId: string;
  /** The per-occurrence identifier. */
  alarmId: string;
  action: AlarmAssignmentAction;
}

/**
 * Operator-facing copy for a failed claim or release.
 *
 * A 404 has two readings: the BFF's own "Alarm not found" (the alarm was
 * replaced by a new one, e.g. a closed alarm that fired again) or a 404 from
 * somewhere else, such as a BFF or gateway that does not serve the assignment
 * route. Only the first means the alarm is gone.
 */
export function alarmAssignmentErrorMessage(error: unknown, action: AlarmAssignmentAction): string {
  const verb = action === 'assign_to_me' ? 'assign this alarm to you' : 'release this alarm';
  const status = error instanceof ApiError ? error.status : undefined;
  if (status === 404) {
    return /alarm not found/i.test(error instanceof Error ? error.message : '')
      ? `This alarm no longer exists, so Helios could not ${verb}.`
      : `Helios could not ${verb}: the assignment service did not answer this request. Try again later.`;
  }
  if (status === 401 || status === 403) {
    return `You do not have permission to ${verb}.`;
  }
  const message = error instanceof Error ? error.message : '';
  if (
    (status === undefined && /fetch|network|timed? ?out/i.test(message)) ||
    (status ?? 0) >= 500
  ) {
    return `Helios could not ${verb}: the service is unavailable. Try again in a moment.`;
  }
  return message || `Could not ${verb}.`;
}

export function useAlarmAssignment() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, AlarmAssignmentVars>({
    mutationFn: ({ siteId, alarmId, action }) => sendAlarmAssignment(siteId, alarmId, action),
    onSuccess: (_data, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: ['alarm', siteId] });
      queryClient.invalidateQueries({ queryKey: ['alarms', siteId] });
      queryClient.invalidateQueries({ queryKey: ['console-alarms'] });
    },
  });
}
